/**
 * Calculates the completion percentage of fertility testing section based on patient data
 *
 * Fields counted:
 * - Whether testing has been done (yes/no answer)
 * - Test results (if any tests have been recorded)
 */

import type { Patient } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";

/**
 * Calculate Fertility Testing completion.
 * Returns: { percent 0..1, label "x/y", completed, total }
 */
type FieldStatus = {
  name: string;
  filled: boolean;
  value: unknown;
  na?: boolean;
};

export function calculateFertilityTestingCompletion(
  patient: Patient | null | undefined,
): {
  percent: number;
  label: string;
  completed: number;
  total: number;
  breakdown?: {
    filled: FieldStatus[];
    missing: FieldStatus[];
    notApplicable: FieldStatus[];
  };
} {
  // Check both possible locations for test results
  const fertilityHistory = patient?.fertilityHistory as
    { testResults?: Patient["testResults"] } | null | undefined;
  const testResults = fertilityHistory?.testResults ?? patient?.testResults;

  const statuses: FieldStatus[] = [];

  // 1. Has testing been addressed? (either has test results or explicitly none)
  const hasTestingData =
    testResults !== undefined &&
    testResults !== null &&
    testResults.length >= 0;

  statuses.push({
    name: "testingStatus",
    filled: hasTestingData,
    value: testResults,
  });

  // 2. Has at least one test result (if testing was done)
  if (testResults && testResults.length > 0) {
    statuses.push({
      name: "hasTestResults",
      filled: true,
      value: testResults.length,
    });
  } else if (hasTestingData && testResults?.length === 0) {
    // Explicitly no tests - that's complete
    statuses.push({
      name: "hasTestResults",
      filled: true,
      value: "no tests done",
      na: true,
    });
  } else {
    // No data at all
    statuses.push({
      name: "hasTestResults",
      filled: false,
      value: null,
    });
  }

  // Calculate percentage based on required fields ONLY (exclude n/a)
  const requiredStatuses = statuses.filter((s) => !s.na);
  const completedFields = requiredStatuses.filter((s) => s.filled).length;
  const totalFields = requiredStatuses.length;

  const percent = totalFields > 0 ? completedFields / totalFields : 0;

  const filledList = statuses.filter((s) => s.filled && !s.na);
  const missingList = statuses.filter((s) => !s.filled && !s.na);
  const notApplicableList = statuses.filter((s) => s.na);

  return {
    percent,
    label: `${completedFields}/${totalFields}`,
    completed: completedFields,
    total: totalFields,
    breakdown: {
      filled: filledList,
      missing: missingList,
      notApplicable: notApplicableList,
    },
  };
}
